import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';
import {
  CONSUMER_SEARCH_NEW_SILHOUETTES,
  CONSUMER_SEARCH_SILHOUETTES_REDIRECTS,
  CONSUMER_SEARCH_SILHOUETTES_REVERSE_REDIRECTS,
} from 'brand/enum/consumerSearchRedirects';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { slugify } from 'shared/utils/consumerSearchSlugify';

const TP = 'pages.pageSlug.silhouette.silhouette';

const SilhouettePage = ({
  brandDisplayName,
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  consumerSearchFilters,
  displayName,
  globalPricingId,
  silhouette,
  isCrawler = false,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      brandName={brandName}
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: brandDisplayName,
          slug: `/${category}/brand/${slugify(brandName)}`,
        },
        {
          name: startCase(silhouette),
          slug: `/${category}/silhouette/${slugify(silhouette)}`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      category={category}
      constructorResponse={constructorResponse}
      consumerSearchFilters={consumerSearchFilters}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      hasBreadcrumbSchema
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      silhouette={silhouette}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, req, res, locale }) => {
  const { pageSlug: category, silhouette }: { pageSlug: string; silhouette: string } = query;
  const q = QueryString.parse(query);
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.SILHOUETTE}/${CONSUMER_SEARCH_SILHOUETTES_REVERSE_REDIRECTS[silhouette] || silhouette}`,
  });

  const browseName: string = startCase(silhouette);
  const displayName = t(`${TP}.displayName`, '{browseName} Sneakers', { browseName });
  const isCrawler = isSearchCrawler(req?.headers);

  const consumerSearchFilters = {
    silhouettes: CONSUMER_SEARCH_NEW_SILHOUETTES[silhouette] || silhouette,
    categories: 'footwear',
  };

  if (CONSUMER_SEARCH_SILHOUETTES_REDIRECTS[silhouette]) {
    return {
      redirect: {
        destination: getRedirectionPath(
          `/${category}/silhouette/${CONSUMER_SEARCH_SILHOUETTES_REDIRECTS[silhouette]}`,
          locale,
        ),
        permanent: false,
      },
    };
  }
  // If consumer search is enabled, we need to fetch the consumer data details
  const consumerSearchInitialData = await getConsumerSearchServerSideData({
    filters: { ...q, pageFilters: consumerSearchFilters },
    isCrawler,
  });

  if (!consumerSearchInitialData?.items?.hasData) {
    return { notFound: true };
  }

  const brandsList = consumerSearchInitialData?.aggregations?.list?.find(
    (a) => a.bucketName === 'brands',
  );
  const brandWithHighestCount = brandsList?.valuesList?.reduce((max, current) =>
    current.count > max.count ? current : max,
  );

  return {
    props: {
      ...(await setServerSideTranslations({ locale })),
      brandName: brandWithHighestCount?.slug || '',
      brandDisplayName: brandWithHighestCount?.value || '',
      breadcrumbs,
      browseKey: 'silhouette',
      browseValue: browseName?.toLowerCase(),
      category,
      constructorResponse: {},
      displayName,
      globalPricingId,
      silhouette,
      consumerSearchFilters,
      consumerSearchInitialData: JSON.stringify({}),
      isCrawler,
    },
  };
};

export default SilhouettePage;
